import { toast } from "react-toastify";
import { useMutation, useQuery } from "@tanstack/react-query";
import { EmailTemplateService } from "@/api";

/**
 * Default error handler to display error messages using react-toastify.
 * @param {object} err - The error object from the mutation.
 */
const defaultError = (err) => {
  const message = err?.response?.data?.message || err?.message || "An error occurred. Please try again.";
  toast.error(message);
};

/**
 * Custom hook for fetching email template list.
 * It utilizes the `useQuery` hook to retrieve email template data.
 *
 * @param {Object} params - Query parameters (page, limit, search_text)
 * @param {function} onSuccess - Callback to execute when the query is successful.
 * @param {function} [onError=defaultError] - Callback to execute when the query fails.
 *
 * @returns {object} Query object from `useQuery`.
 */
const useListEmailTemplate = (params = {}, onSuccess, onError = defaultError) => {
  return useQuery({
    queryKey: ["email-template-list", params], // Query key
    queryFn: () => {
      return EmailTemplateService.getList(params);
    }, // Query function
    onSuccess: (data) => {
      if (onSuccess) onSuccess(data);
    }, // Success callback
    onError: (error) => {
      onError(error);
    }, // Error callback
    retry: 2, // Allow retry for transient failures
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000), // Exponential backoff
    staleTime: 30000, // Consider data fresh for 30 seconds
    cacheTime: 300000, // Cache for 5 minutes
    refetchOnWindowFocus: false, // Don't refetch on window focus
    enabled: Object.keys(params).length > 0, // Only run when params are provided
  });
};

/**
 * Custom hook for fetching a single email template.
 * It utilizes the `useQuery` hook to retrieve email template data by UUID.
 *
 * @param {string} uuid - Email Template UUID
 * @param {function} onSuccess - Callback to execute when the query is successful.
 * @param {function} [onError=defaultError] - Callback to execute when the query fails.
 *
 * @returns {object} Query object from `useQuery`.
 */
const useGetEmailTemplate = (uuid, onSuccess, onError = defaultError) => {
  return useQuery({
    queryKey: ["email-template", uuid], // Query key
    queryFn: () => {
      return EmailTemplateService.get(uuid);
    }, // Query function
    onSuccess: (data) => {
      if (onSuccess) onSuccess(data);
    }, // Success callback
    onError: (error) => {
      onError(error);
    }, // Error callback
    retry: 2, // Allow retry for transient failures
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000), // Exponential backoff
    staleTime: 30000, // Consider data fresh for 30 seconds
    cacheTime: 300000, // Cache for 5 minutes
    refetchOnWindowFocus: false, // Don't refetch on window focus
    enabled: !!uuid, // Only run when uuid is provided
  });
};

/**
 * Custom hook for adding a new email template.
 * It uses the `useMutation` hook to send the add request and handle the response.
 *
 * @param {function} onSuccess - Callback to run if the add request succeeds.
 * @param {function} [onError=defaultError] - Callback to run if the add request fails.
 *
 * @returns {object} Mutation object from `useMutation`.
 */
const useAddEmailTemplate = (onSuccess, onError = defaultError) => {
  return useMutation({
    mutationFn: (data) => {
      return EmailTemplateService.add(data);
    },
    onSuccess,
    onError,
  });
};

/**
 * Custom hook for updating an existing email template.
 * It uses the `useMutation` hook to send the update request and handle the response.
 *
 * @param {function} onSuccess - Callback to run if the update request succeeds.
 * @param {function} [onError=defaultError] - Callback to run if the update request fails.
 *
 * @returns {object} Mutation object from `useMutation`.
 */
const useUpdateEmailTemplate = (onSuccess, onError = defaultError) => {
  return useMutation({
    mutationFn: (data) => {
      return EmailTemplateService.update(data);
    },
    onSuccess,
    onError,
  });
};

/**
 * Custom hook for deleting an email template.
 * It uses the `useMutation` hook to send the delete request and handle the response.
 *
 * @param {function} onSuccess - Callback to run if the delete request succeeds.
 * @param {function} [onError=defaultError] - Callback to run if the delete request fails.
 *
 * @returns {object} Mutation object from `useMutation`.
 */
const useEmailTemplateDelete = (onSuccess, onError = defaultError) => {
  return useMutation({
    mutationFn: (uuid) => {
      return EmailTemplateService.delete(uuid);
    },
    onSuccess,
    onError,
  });
};

export {
  useListEmailTemplate,
  useGetEmailTemplate,
  useAddEmailTemplate,
  useUpdateEmailTemplate,
  useEmailTemplateDelete,
};
